import { FormEvent, useCallback, useEffect, useState } from 'react';
import { Button, Input, Spinner } from '../design-system';
import { PasswordRules } from '../auth/PasswordRules';
import { friendlyPasswordError, passwordProblem } from '../../lib/passwordPolicy';
import { supabase } from '../../lib/supabase';
import type { OAuthProvider } from '../../lib/oauth';
import {
  PROVIDER_LABEL,
  canDisconnect,
  connectProvider,
  disconnectProvider,
  friendlyLinkError,
  loadSignInMethods,
  savePassword,
  type SignInMethods as Methods,
} from '../../lib/signInMethods';

const PROVIDERS: OAuthProvider[] = ['google', 'apple'];

/**
 * Settings → Sign-in methods: one account, several ways in. Shows which of
 * password, Google and Apple are connected, lets the person add or remove
 * them, and never lets them remove the last one.
 */
export function SignInMethods() {
  const [methods, setMethods] = useState<Methods | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [confirmOff, setConfirmOff] = useState<OAuthProvider | null>(null);
  const [editingPassword, setEditingPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const load = useCallback(async () => {
    try {
      setMethods(await loadSignInMethods());
    } catch (e) {
      setError(e instanceof Error ? e.message : "We couldn't load your sign-in methods.");
      setMethods((m) => m ?? { hasPassword: false, providers: [], identities: [] });
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // On a phone the connect round trip finishes in another screen; reload
  // when the session changes or the app comes back into view.
  useEffect(() => {
    const { data } = supabase.auth.onAuthStateChange(() => { void load(); });
    const onVisible = () => { if (document.visibilityState === 'visible') void load(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      data.subscription.unsubscribe();
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load]);

  async function connect(provider: OAuthProvider) {
    setBusy(provider);
    setError(null);
    setDone(null);
    try {
      await connectProvider(provider);
      // On the web the page is already leaving for the provider.
    } catch (e) {
      setError(friendlyLinkError(e, provider) ?? (e instanceof Error ? e.message : `We couldn't connect ${PROVIDER_LABEL[provider]}.`));
      setBusy(null);
    }
  }

  async function disconnect(provider: OAuthProvider) {
    const identity = methods?.identities.find((i) => i.provider === provider);
    if (!identity) return;
    setBusy(provider);
    setError(null);
    try {
      await disconnectProvider(identity);
      setConfirmOff(null);
      setDone(`${PROVIDER_LABEL[provider]} is disconnected.`);
      await load();
    } catch (e) {
      setError(friendlyLinkError(e, provider) ?? (e instanceof Error ? e.message : `We couldn't disconnect ${PROVIDER_LABEL[provider]}.`));
    }
    setBusy(null);
  }

  async function submitPassword(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const problem = passwordProblem(password);
    if (problem) { setError(problem); return; }
    if (password !== confirmPassword) { setError("Passwords don't match."); return; }
    setBusy('password');
    try {
      await savePassword(password);
      setEditingPassword(false);
      setPassword('');
      setConfirmPassword('');
      setDone(methods?.hasPassword ? 'Your password is changed.' : 'Your password is set. You can now sign in with it too.');
      await load();
    } catch (err) {
      setError(friendlyPasswordError(err) ?? (err instanceof Error ? err.message : "We couldn't save your password."));
    }
    setBusy(null);
  }

  if (methods === null) return <div className="flex justify-center py-4"><Spinner /></div>;

  const emailOf = (p: OAuthProvider) => {
    const data = methods.identities.find((i) => i.provider === p)?.identity_data as { email?: string } | undefined;
    return data?.email ?? null;
  };

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-app-muted">
        Sign in the way that suits you. Connect more than one and any of them opens this same account, with all your trips and chats.
      </p>

      {done && <p role="status" className="text-sm text-emerald-500">{done}</p>}
      {error && <p role="alert" className="text-sm text-crimson">{error}</p>}

      <ul className="flex flex-col divide-y divide-app-border rounded-xl border border-app-border">
        <li className="flex flex-col gap-3 p-3">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-sm font-medium text-app-fg">Password</div>
              <div className="text-xs text-app-muted">{methods.hasPassword ? 'Set' : 'Not set'}</div>
            </div>
            {!editingPassword && (
              <Button size="sm" variant="secondary" onClick={() => { setEditingPassword(true); setDone(null); setError(null); }} disabled={busy !== null}>
                {methods.hasPassword ? 'Change' : 'Set a password'}
              </Button>
            )}
          </div>
          {editingPassword && (
            <form onSubmit={(e) => void submitPassword(e)} className="flex flex-col gap-3">
              <Input
                id="signin-password"
                type="password"
                label={methods.hasPassword ? 'New password' : 'Password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                aria-describedby="signin-password-rules"
                required
              />
              <PasswordRules password={password} id="signin-password-rules" />
              <Input
                id="signin-confirm-password"
                type="password"
                label="Confirm password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
              <div className="flex gap-2">
                <Button size="sm" type="submit" disabled={busy !== null}>
                  {busy === 'password' ? <Spinner size="sm" className="text-ink" /> : 'Save password'}
                </Button>
                <Button size="sm" variant="ghost" type="button" onClick={() => { setEditingPassword(false); setPassword(''); setConfirmPassword(''); setError(null); }}>
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </li>

        {PROVIDERS.map((p) => {
          const connected = methods.providers.includes(p);
          const email = connected ? emailOf(p) : null;
          return (
            <li key={p} className="flex flex-col gap-2 p-3">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-app-fg">{PROVIDER_LABEL[p]}</div>
                  <div className="text-xs text-app-muted truncate">
                    {connected ? (email ? `Connected · ${email}` : 'Connected') : 'Not connected'}
                  </div>
                </div>
                {connected ? (
                  canDisconnect(methods, p) ? (
                    confirmOff !== p && (
                      <Button size="sm" variant="ghost" onClick={() => { setConfirmOff(p); setDone(null); setError(null); }} disabled={busy !== null}>
                        Disconnect
                      </Button>
                    )
                  ) : (
                    <span className="text-xs text-app-muted">Your only sign-in method</span>
                  )
                ) : (
                  <Button size="sm" variant="secondary" onClick={() => void connect(p)} disabled={busy !== null}>
                    {busy === p ? <Spinner size="sm" /> : 'Connect'}
                  </Button>
                )}
              </div>
              {confirmOff === p && (
                <div className="flex flex-wrap items-center gap-2 rounded-lg bg-app-bg p-3">
                  <span className="text-sm text-app-fg">
                    Disconnect {PROVIDER_LABEL[p]}? You'll sign in with your other methods.
                  </span>
                  <Button size="sm" onClick={() => void disconnect(p)} disabled={busy !== null}>
                    {busy === p ? <Spinner size="sm" className="text-ink" /> : 'Yes, disconnect'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setConfirmOff(null)}>Cancel</Button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
